import Comments from '@/components/company-comments/Comments.vue';
import DashboardAssignedActions from '@/components/dashboard/DashboardAssignedActions.vue';
import DashboardNameActions from '@/components/dashboard/DashboardNameActions.vue';
import TableDateSelect from '@/components/dashboard/TableDateSelect.vue';
import TableReloadSelect from '@/components/dashboard/TableReloadSelect.vue';
import TableStatusSelect from '@/components/dashboard/TableStatusSelect.vue';
import DataTableColumnHeader from '@/components/datatable/DataTableColumnHeader.vue';
import DataTableColumnHeaderWithBulk from '@/components/datatable/DataTableColumnHeaderWithBulk.vue';
import DataTableTdCheckbox from '@/components/datatable/DataTableTdCheckbox.vue';
import DataTableThCheckbox from '@/components/datatable/DataTableThCheckbox.vue';
import EmployeeAvatarRowDialog from '@/components/EmployeeAvatarRowDialog.vue';
import OnboardingGuide from '@/components/onboarding/OnboardingGuide.vue';
import ProgressBar from '@/components/ProgressBar.vue';
import { useDateFormat } from '@/composables/useDateFormat';
import type { DashboardTableData, SelectOption } from '@/types';
import type {
    Column,
    ColumnDef,
    ColumnMeta,
    FilterFnOption,
    Row,
    Table,
} from '@tanstack/vue-table';
import { h } from 'vue';

const { formatDMYFromIso } = useDateFormat();

const createStatusSelect = (
    accessorKey: keyof DashboardTableData,
    statusOptions: Record<string, SelectOption[]>,
    reportingPeriod: string,
    selectedGrouping: string,
) => ({
    accessorKey: accessorKey,
    header: ({
        column,
        table,
    }: {
        column: Column<DashboardTableData, unknown>;
        table: Table<DashboardTableData>;
    }) =>
        h(DataTableColumnHeaderWithBulk, {
            column: column,
            table: table,
            title: `dashboards.datatable.${accessorKey}`,
            statusOptions: statusOptions[accessorKey] || [],
            dataFieldGroup: accessorKey,
            reportingPeriod: reportingPeriod,
        }),
    cell: ({ row }: { row: Row<DashboardTableData> }) => {
        return h(TableStatusSelect, {
            row: row,
            columnId: accessorKey,
            options: statusOptions[accessorKey] || [],
            selectedGrouping: selectedGrouping,
            reportingPeriod: reportingPeriod,
        });
    },
    filterFn: 'arrIncludesSome' as FilterFnOption<DashboardTableData>,
    meta: {
        filterType: 'checkbox' as ColumnMeta<DashboardTableData, any>['filterType'],
    },
});

export const columns = ({
    statusOptions,
    reportingPeriod,
    selectedGrouping,
}: {
    statusOptions: Record<string, SelectOption[]>;
    reportingPeriod: string;
    selectedGrouping: string;
}): ColumnDef<DashboardTableData>[] => [
    {
        accessorKey: 'select',
        header: ({ table }) => h(DataTableThCheckbox, { table }),
        cell: ({ row }) => h(DataTableTdCheckbox, { row }),
        enableHiding: false,
        size: 24,
        enableColumnFilter: false,
    },
    {
        accessorKey: 'id',
        enableHiding: false,
        filterFn: 'valueInArray',
        header: () => null,
        cell: () => null,
    },
    {
        accessorKey: 'name',
        enableHiding: false,
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.name',
            }),
        cell: ({ row }) =>
            h('div', { class: 'flex w-full items-center gap-2' }, [
                h(Comments, {
                    company: row.original,
                    commentCount: row.original.comment_count,
                }),
                h('span', { class: 'min-w-0 flex-1 truncate' }, row.original.name),
                row.original.id
                    ? h(DashboardNameActions, {
                          companyId: row.original.id,
                          refreshDashboard: true,
                      })
                    : null,
            ]),

        sortingFn: 'text',
    },
    {
        accessorKey: 'keepers_employees',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.keepers_employees',
            }),
        cell: ({ row }) =>
            h('div', { class: 'grid w-full grid-cols-[minmax(0,1fr)_1.5rem] items-center gap-1' }, [
                h('div', { class: 'min-w-0 overflow-hidden' }, [
                    h(EmployeeAvatarRowDialog, {
                        employees: row.original.keepers_employees,
                        company: row.original.name,
                    }),
                ]),
                row.original.id
                    ? h(DashboardAssignedActions, {
                          companyId: row.original.id,
                          refreshDashboard: true,
                      })
                    : null,
            ]),
        filterFn: 'arrIncludesSome',
        meta: {
            filterType: 'checkbox',
        },
    },
    createStatusSelect('bookkeeping_status', statusOptions, reportingPeriod, selectedGrouping),
    {
        accessorKey: 'bookkeeping_deadline',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.bookkeeping_deadline',
            }),
        cell: ({ row }) => {
            return formatDMYFromIso(row.original.bookkeeping_deadline);
        },
        filterFn: 'dateRange',
    },
    createStatusSelect('report_status', statusOptions, reportingPeriod, selectedGrouping),
    {
        accessorKey: 'report_deadline',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.report_deadline',
            }),
        cell: ({ row }) => {
            return formatDMYFromIso(row.original.report_deadline);
        },
        filterFn: 'dateRange',
    },
    createStatusSelect('vat_status', statusOptions, reportingPeriod, selectedGrouping),
    {
        accessorKey: 'vat_deadline',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.vat_deadline',
            }),
        cell: ({ row }) => {
            return formatDMYFromIso(row.original.vat_deadline);
        },
        filterFn: 'dateRange',
    },
    createStatusSelect('pbc_status', statusOptions, reportingPeriod, selectedGrouping),
    {
        accessorKey: 'pbc_deadline',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.pbc_deadline',
            }),
        cell: ({ row }) => {
            return h(TableDateSelect, {
                key: row.original.id,
                row: row,
                reportingPeriod: reportingPeriod,
                selectedGrouping: selectedGrouping,
                columnId: 'pbc_deadline',
            });
        },
    },
    createStatusSelect('annual_report', statusOptions, reportingPeriod, selectedGrouping),
    {
        accessorKey: 'accouting_year',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.accouting_year',
            }),
        cell: ({ row }) => {
            return formatDMYFromIso(row.original.accouting_year);
        },
    },
    {
        accessorKey: 'onboarding',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.onboarding',
            }),
        cell: ({ row }) =>
            !row.original.is_onboarding_completed
                ? h(OnboardingGuide, {
                      onboardingSteps: row.original.onboarding,
                      companyId: row.original.id,
                  })
                : null,
        enableColumnFilter: false,
    },
    {
        accessorKey: 'vat_frequency',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.vat_frequency',
            }),
        filterFn: 'arrIncludesSome',
        meta: {
            filterType: 'checkbox',
        },
    },
    {
        accessorKey: 'weekday',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.weekday',
            }),
        cell: ({ row }) =>
            h(
                'div',
                { class: 'w-[200px] whitespace-normal break-words leading-none' },
                row.original.weekday,
            ),
        filterFn: 'arrIncludesSome',
        meta: {
            filterType: 'checkbox',
        },
    },
    {
        accessorKey: 'update_frequency',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.update_frequency',
            }),
        filterFn: 'arrIncludesSome',
        meta: {
            filterType: 'checkbox',
        },
    },
    {
        accessorKey: 'postings',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.postings',
            }),
        cell: ({ row }) =>
            h(ProgressBar, {
                progress: row.original.postings,
            }),
        enableColumnFilter: false,
    },
    {
        accessorKey: 'actual_vs_contract',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.actual_vs_contract',
            }),
        enableColumnFilter: false,
    },
    {
        accessorKey: 'active_services',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.active_services',
            }),
        cell: ({ row }) =>
            h(
                'div',
                { class: 'w-[200px] whitespace-normal break-words leading-none' },
                row.original.active_services,
            ),
    },
    {
        accessorKey: 'handled_vouchers',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.handled_vouchers',
            }),
        enableColumnFilter: false,
    },
    {
        accessorKey: 'reload_on_demand',
        header: ({ column, table }) =>
            h(DataTableColumnHeader, {
                column: column,
                table: table,
                title: 'dashboards.datatable.reload_on_demand',
            }),
        cell: ({ row }) =>
            h(TableReloadSelect, {
                modelValue: row.original.reload_on_demand,
                companyId: row.original.id,
                row: row,
            }),
        filterFn: 'arrIncludesSome',
        meta: {
            filterType: 'checkbox',
        },
    },
];
